import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";

import { WFPButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";

import { type SchoolBid } from "../../../BidRequests/types";
import { formatMoney } from "../../../BidRequests/utils";

interface AcceptBidDialogProps {
  bid: SchoolBid | null;
  isAccepting: boolean;
  errorMessage: string;
  onCancel: () => void;
  onConfirm: (bid: SchoolBid) => void;
}

export const AcceptBidDialog = ({
  bid,
  isAccepting,
  errorMessage,
  onCancel,
  onConfirm,
}: AcceptBidDialogProps) => {
  const { t } = useTranslation();

  if (!bid) {
    return null;
  }

  return (
    <Dialog open onOpenChange={open => !open && !isAccepting && onCancel()}>
      <DialogContent
        className="sm:max-w-md"
        aria-describedby={undefined}
        showCloseButton={!isAccepting}
      >
        <DialogHeader>
          <DialogTitle>{t("bidRequests.detail.acceptDialog.title")}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 text-sm">
          <p>{t("bidRequests.detail.acceptDialog.intro")}</p>
          <p className="font-semibold">{bid.aggregator_name}</p>
          <p className="text-muted-foreground">{t("bidRequests.detail.acceptDialog.forTotal")}</p>
          <p className="font-semibold">{formatMoney(bid.currency, bid.total)}</p>
          {errorMessage && <p className="text-destructive">{errorMessage}</p>}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <WFPButton
            type="button"
            variant="success"
            className="w-full"
            disabled={isAccepting}
            onClick={() => onConfirm(bid)}
          >
            {isAccepting ? <Spinner className="size-4" /> : <Check className="size-4" />}
            {t("bidRequests.detail.acceptDialog.confirm")}
          </WFPButton>
          <WFPButton
            type="button"
            variant="outline"
            className="w-full"
            disabled={isAccepting}
            onClick={onCancel}
          >
            {t("bidRequests.detail.acceptDialog.cancel")}
          </WFPButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
